import { cn } from '@/lib/shadcn/utils';
import { Textarea } from '@/modules/ui/components/textarea/Textarea';

export type AnnotationReason =
  | 'skills'
  | 'culture'
  | 'seniority'
  | 'domain'
  | 'strong_profile'
  | 'weak_profile'
  | 'location'
  | 'other';

const POSITIVE_REASONS: { value: AnnotationReason; label: string }[] = [
  { value: 'skills', label: 'Skills' },
  { value: 'culture', label: 'Culture fit' },
  { value: 'seniority', label: 'Seniority' },
  { value: 'domain', label: 'Domain expertise' },
  { value: 'strong_profile', label: 'Strong profile' },
  { value: 'other', label: 'Other' },
];

const NEGATIVE_REASONS: { value: AnnotationReason; label: string }[] = [
  { value: 'skills', label: 'Skills' },
  { value: 'culture', label: 'Culture fit' },
  { value: 'seniority', label: 'Seniority' },
  { value: 'domain', label: 'Domain expertise' },
  { value: 'weak_profile', label: 'Weak profile' },
  { value: 'location', label: 'Location' },
  { value: 'other', label: 'Other' },
];

interface AnnotationPickerProps {
  isPositive: boolean;
  selectedReasons: AnnotationReason[];
  onReasonsChange: (reasons: AnnotationReason[]) => void;
  otherText: string;
  onOtherTextChange: (text: string) => void;
}

export function AnnotationPicker({
  isPositive,
  selectedReasons,
  onReasonsChange,
  otherText,
  onOtherTextChange,
}: AnnotationPickerProps) {
  const reasons = isPositive ? POSITIVE_REASONS : NEGATIVE_REASONS;

  const toggle = (value: AnnotationReason) => {
    if (selectedReasons.includes(value)) {
      onReasonsChange(selectedReasons.filter((r) => r !== value));
    } else {
      onReasonsChange([...selectedReasons, value]);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1.5">
        {reasons.map(({ value, label }) => {
          const isSelected = selectedReasons.includes(value);
          return (
            <button
              key={value}
              type="button"
              onClick={() => toggle(value)}
              className={cn(
                'rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                isSelected
                  ? isPositive
                    ? 'bg-semantic-560/10 border-transparent text-neutral-800'
                    : 'bg-semantic-550/10 border-transparent text-neutral-800'
                  : 'border-neutral-200 bg-neutral-50 text-neutral-600 hover:border-neutral-300 hover:bg-neutral-100 hover:text-neutral-800',
              )}
            >
              {label}
            </button>
          );
        })}
      </div>

      {selectedReasons.includes('other') && (
        <Textarea
          placeholder="What's the reason?"
          value={otherText}
          onChange={(e) => onOtherTextChange(e.target.value)}
          className="min-h-20 resize-none"
          autoFocus
        />
      )}
    </div>
  );
}

export function buildAnnotation(
  reasons: AnnotationReason[],
  otherText: string,
): string | null {
  if (reasons.length === 0) return null;

  const allReasons = [...POSITIVE_REASONS, ...NEGATIVE_REASONS];
  const parts = reasons.map((r) => {
    if (r === 'other') return otherText.trim();
    return allReasons.find((x) => x.value === r)?.label ?? r;
  });

  const filtered = parts.filter(Boolean);
  return filtered.length > 0 ? filtered.join(', ') : null;
}
